import { Icon, Link, Text } from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import styled from 'styled-components/macro'

import { formatNameWithCode } from 'utils/formatters'
import { encodeURIComponentImageName, getSizedImageUrl } from 'utils/images'
import { fromGlobalId } from 'utils/transformations/graphql'

import { ColorsList } from '../ColorsList/ColorsList'
import { Quantity } from '../ColorsList/components/Quantity'
import messages from 'features/Orders/Review/components/Overview/components/ProductsWidget/constants/messages'
import { Product } from 'features/Orders/Review/components/Overview/components/ProductsWidget/types'

type Totals = {
  totalStyleQty: number
  totalStyleCost: string
  styleDiscount?: string
  finalStyleCost?: string
}

function ProductTotals({
  totals: { totalStyleQty, totalStyleCost, styleDiscount, finalStyleCost },
}: {
  totals: Totals
}) {
  return (
    <>
      <StyledQuantityHolder data-column-name="qty-and-cost">
        <Quantity quantity={totalStyleQty} label={messages.totalStyleQty} />
        <Quantity
          className="cost"
          quantity={totalStyleCost}
          label={messages.totalStyleCost}
        />
      </StyledQuantityHolder>
      {styleDiscount && finalStyleCost && (
        <StyledQuantityHolder data-column-name="qty-and-cost">
          <Quantity quantity={styleDiscount} label={messages.styleDiscount} />
          <Quantity
            className="cost"
            quantity={finalStyleCost}
            label={messages.finalStyleCost}
          />
        </StyledQuantityHolder>
      )}
    </>
  )
}

function ProductComment({ productComment }: { productComment?: string }) {
  if (!productComment) {
    return null
  }

  return (
    <BreakLineText
      fontSize="small"
      fontWeight="semibold"
      lineHeight="16"
      letterSpacing="tighter"
    >
      {`${messages.styleComments} `}
      <Text
        fontSize="small"
        fontWeight="medium"
        lineHeight="16"
        letterSpacing="tighter"
        className="notranslate break-line"
        as="span"
      >
        {productComment}
      </Text>
    </BreakLineText>
  )
}

export function ProductItem({
  product: {
    id,
    name,
    imageUrl,
    externalIdentifier,
    wholesalePrice,
    retailPrice,
    colors,
    productComment,
    quantity,
    totalStyleCost,
    styleDiscount,
    finalStyleCost,
    attributes,
    countryOfOrigin,
    collection,
    brandId,
  },
}: Props) {
  const { newCollectionsEditAndCreate } = useFlags()

  const productLink = `${window.location.origin}/Styles/view/${
    fromGlobalId(id).id
  }/${fromGlobalId(collection?.id).id}`

  const attributesWithValues = attributes?.filter(
    (attribute) => !!attribute.value,
  )

  const linesheetLink = newCollectionsEditAndCreate
    ? `${window.location.origin}/ra/linesheet/${
        fromGlobalId(collection?.id).id
      }`
    : `${window.location.origin}/collections/view/${
        fromGlobalId(collection?.id).id || ''
      }?brandId=${fromGlobalId(brandId).id}`

  // For the case when we have only one color, we need to get the image of that
  // specific color instead of showing the main image
  const colorImage =
    colors.length === 1 ? colors[0].merchandiseImages?.[0]?.url : undefined

  return (
    <StyledItem>
      {imageUrl ? (
        <StyledImage
          src={getSizedImageUrl(
            encodeURIComponentImageName(colorImage || imageUrl),
            '120x160',
          )}
          alt={name}
          loading="lazy"
        />
      ) : (
        <StyledIcon
          data-testid={ProductItemTestIds.ImgPlaceholder}
          iconName="image"
          plain
        />
      )}
      <InfoContainer>
        <div className="attributes">
          <div className="row">
            <Text
              fontSize="small"
              fontWeight="medium"
              lineHeight="16"
              letterSpacing="tighter"
              className="notranslate"
            >
              {name}
            </Text>
            <Text fontFamily="numeric" fontSize="small" fontWeight="semibold">
              <Text as={'span'} fontSize="small" fontWeight="medium">
                {messages.wholesale}
              </Text>{' '}
              {wholesalePrice.label}
            </Text>
          </div>
          <div className="row">
            <div>
              {!!collection ? (
                <Link
                  href={productLink}
                  target="_blank"
                  fontFamily="numeric"
                  fontSize="base"
                  fontWeight="semibold"
                  lineHeight="16"
                  letterSpacing="normal"
                  className="notranslate"
                >
                  {externalIdentifier}
                </Link>
              ) : (
                <Text
                  fontFamily="numeric"
                  fontSize="base"
                  fontWeight="semibold"
                  lineHeight="16"
                  letterSpacing="normal"
                  className="notranslate"
                >
                  {externalIdentifier}
                </Text>
              )}
              {!!collection && (
                <>
                  {' | '}
                  <Link
                    href={linesheetLink}
                    target="_blank"
                    fontFamily="numeric"
                    fontSize="base"
                    fontWeight="semibold"
                    lineHeight="16"
                    letterSpacing="normal"
                    className="notranslate"
                  >
                    {collection.name}
                  </Link>
                </>
              )}
            </div>
            <Text fontFamily="numeric" fontSize="small" fontWeight="semibold">
              <Text as={'span'} fontSize="small" fontWeight="medium">
                {messages.suggRetail}
              </Text>{' '}
              {retailPrice.label}
            </Text>
          </div>
          {attributes && attributes.length > 0 && (
            <div className="row">
              <Text fontSize="small" fontWeight="medium" color="primary-500">
                <Text
                  as={'span'}
                  fontSize="small"
                  fontWeight="semibold"
                  color="primary-500"
                >
                  {messages.attributes}
                </Text>{' '}
                {attributesWithValues && attributesWithValues?.length > 0
                  ? attributesWithValues?.map((attribute, index) => (
                      <span key={`attribute-${index}`}>
                        {attribute.name}:{' '}
                        <span className="notranslate">
                          {formatNameWithCode(
                            attribute.value,
                            attribute.externalIdentifier,
                          )}
                        </span>
                        {index !== attributesWithValues?.length - 1 ? (
                          <Text
                            fontSize="small"
                            as={'span'}
                            fontWeight="medium"
                          >
                            {' | '}
                          </Text>
                        ) : undefined}
                      </span>
                    ))
                  : ' - '}
              </Text>
            </div>
          )}
          {countryOfOrigin && (
            <div className="row">
              <Text fontSize="small" fontWeight="medium" color="primary-500">
                <Text
                  as={'span'}
                  fontSize="small"
                  fontWeight="semibold"
                  color="primary-500"
                >
                  {messages.countryOfOrigin}
                </Text>{' '}
                {countryOfOrigin}
              </Text>
            </div>
          )}
        </div>
        <ColorsList colors={colors} testId={ProductItemTestIds.ColorsList} />
        <ProductComment productComment={productComment} />
        <ProductTotals
          totals={{
            totalStyleQty: quantity,
            totalStyleCost,
            styleDiscount,
            finalStyleCost,
          }}
        />
      </InfoContainer>
    </StyledItem>
  )
}

export const ProductItemTestIds = {
  ImgPlaceholder: 'product-item-img-placeholder',
  ColorsList: 'product-item-colors-list',
} as const

type Props = {
  product: Product
}

const StyledItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 16px 10px 10px;
  border-bottom: 1px solid var(--color-primary-100);
`

const StyledImage = styled.img`
  width: 60px;
  object-fit: contain;
  filter: brightness(92%);
`

const InfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .attributes {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      p {
        margin: 0;
      }
    }
  }
`

const StyledIcon = styled(Icon)`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--size-border-radius-small);
  background-color: var(--color-secondary-100);
`

const StyledQuantityHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: end;

  > *:last-child {
    min-width: 114px;
  }
`

const BreakLineText = styled(Text)`
  .break-line {
    white-space: pre-line;
  }
`
